import {GROUND_BACKGROUNDS} from '../groundBackgrounds';

/**
 * ПРОБУЖДЕНИЕ ЛАБИРИНТОВ — эффект подложки с каменными лабиринтами (spiral_1).
 *
 * На пыльном плато вырезаны лабиринты: четыре бледные спирали по углам поля и
 * кольцо глубоких лабиринтов вокруг него. Раз в несколько секунд один из них
 * «просыпается»: по его бороздам, кольцо за кольцом от середины к краю,
 * пробегает проблеск холодного света. Кольцо не загорается целиком: проблеск
 * обходит его по кругу — по часовой стрелке или против, как выпадет, — и
 * оставляет за собой светящийся обод. Замкнувшись, обод недолго держится и
 * гаснет — и лабиринт снова темен (см. utils/effects/biome/labyrinthGlow).
 *
 * Свет — кольца на самой земле: под дымкой пустых ячеек и под жетонами, жетон
 * закрывает свою часть кольца. Кольца — овалы: мы смотрим на плато под углом, и
 * нарисованные круги сплюснуты (чем выше на картинке, тем сильнее).
 *
 * Чего здесь нельзя: зажигать все лабиринты разом (по полю пошел бы пестрый
 * узор) и красить свет в цвета меток поля (золото, красный, бурый) — холодный
 * голубой с ними не спутать.
 */
export const LABYRINTH_GLOW_KIND = 'labyrinthGlow';

// Спирали на самом поле зажигаются вдвое чаще лабиринтов вокруг него: их
// игрок видит всегда
const INNER_WEIGHT = 2;
const RIM_WEIGHT = 1;

/**
 * Нарисованный лабиринт: середина и полуоси его внешней борозды (доли стороны
 * картинки пейзажа; сверены с картинкой наложением овалов), weight — как часто
 * он просыпается
 */
const labyrinth = (u, v, radiusU, radiusV, weight = RIM_WEIGHT) => Object.freeze({u, v, radiusU, radiusV, weight});

export const LABYRINTH_GLOW_EFFECT = Object.freeze({
  kind: LABYRINTH_GLOW_KIND,
  backgrounds: Object.freeze({
    [GROUND_BACKGROUNDS.SPIRAL_1]: Object.freeze({
      // Первый лабиринт просыпается вскоре после начала; следующий — через
      // паузу от начала предыдущего. Пробуждение длится пять с половиной
      // секунд, поэтому светится один лабиринт, изредка два, не больше
      firstDelayMs: {min: 2500, max: 5000},
      intervalMs: {min: 5000, max: 9000},
      maxGroups: 2,
      // Холодный голубой свет, складывается с цветом камня
      tint: 0x5fd8f8,
      // Яркость замкнувшегося обода самого яркого кольца: ярче — кольцо
      // читалось бы подсветкой ячеек
      peakAlpha: 0.4,
      // Кольца лабиринта от середины к краю: радиус в долях его полуосей и
      // яркость в долях peakAlpha. Свет разгорается к третьему кольцу и
      // слабеет на внешнем
      rings: Object.freeze([
        Object.freeze({radiusShare: 0.3, alphaShare: 0.5}),
        Object.freeze({radiusShare: 0.53, alphaShare: 0.8}),
        Object.freeze({radiusShare: 0.77, alphaShare: 1}),
        Object.freeze({radiusShare: 1, alphaShare: 0.7}),
      ]),
      // Мягкое пятно в середине лабиринта: до какой доли полуосей оно доходит
      // и его яркость в долях peakAlpha
      core: Object.freeze({radiusShare: 0.5, alphaShare: 0.5}),
      // Ширина светящейся полосы обода, размеров ячейки (0.22 — девятая часть
      // ширины ячейки): одна у всех колец, больших и малых
      bandThicknessFactor: 0.22,
      // Обод кольца собран из коротких выгнутых отрезков, уложенных внахлест
      // по нарисованному овалу: спрайт нельзя нарисовать частично, а отрезки
      // загораются один за другим — там, где прошел проблеск
      rim: Object.freeze({
        // Сколько отрезков в ободе, построитель считает сам — по кривизне
        // кольца: от шести на самом малом до восемнадцати на самом большом.
        // Здесь — только пределы: меньше — кольцо выйдет многоугольником,
        // больше — два лабиринта разом не уместятся в бюджет спрайтов
        pieceCount: Object.freeze({min: 6, max: 20}),
        // Только что зажженный отрезок ярче обода на эту долю и остывает до
        // его яркости (втрое за afterglowMs): за проблеском тянется светлый
        // след, по нему видно, куда тот бежит
        flashShare: 0.7,
        afterglowMs: 200,
      }),
      // Проблеск — яркое мягкое пятно на самом фронте света, вытянутое вдоль обода
      gleam: Object.freeze({
        // Во сколько раз он ярче замкнувшегося обода своего кольца
        alphaFactor: 2,
        // Кадр пятна вдоль и поперек обода, размеров ячейки (видимая часть
        // мягкого пятна — около половины кадра)
        lengthFactor: 1.1,
        thicknessFactor: 0.6,
        // Проблеск проступает в начале пути и тает там, где обод замкнулся
        fadeInMs: 150,
        fadeOutMs: 300,
      }),
      wave: Object.freeze({
        // Проходов за одно пробуждение. Один: кольца рисуются неспешно, и
        // второй проход подряд смотрелся бы повтором. Будь их больше, каждый
        // следующий начинался бы через periodMs после начала предыдущего —
        // не раньше, чем погаснет самое долгое кольцо (sweepMs.max + holdMs +
        // decayMs): иначе новый проход оборвал бы угасание прежнего
        count: 1,
        periodMs: 3600,
        // Каждое следующее кольцо начинает рисоваться на столько позже предыдущего
        ringStaggerMs: 600,
        // За сколько проблеск обходит кольцо: внутреннее (min) и внешнее
        // (max), промежуточные — по радиусу. Внешнее втрое длиннее
        // внутреннего: за то же время проблеск по нему несся бы
        sweepMs: Object.freeze({min: 1100, max: 1700}),
        // Замкнувшись, обод держится и медленно гаснет — весь разом
        holdMs: 600,
        decayMs: 1300,
      }),
      // Лабиринт вне кадра просыпается вдесятеро реже: пробуждений мало, и
      // тратить их там, куда игрок не смотрит, жалко. Совсем не гасим — при
      // прокрутке поля лабиринты вокруг не должны оказаться мертвыми
      outOfViewWeightShare: 0.1,
      // Лабиринт считается видимым, если его середина не дальше стольких его
      // полуосей за краем кадра: хотя бы треть лабиринта на экране
      viewReachShare: 0.5,
      labyrinths: Object.freeze([
        // Бледные спирали на самом поле: северо-запад, северо-восток,
        // юго-запад, юго-восток
        labyrinth(0.354, 0.3595, 0.064, 0.057, INNER_WEIGHT),
        labyrinth(0.638, 0.358, 0.067, 0.06, INNER_WEIGHT),
        labyrinth(0.372, 0.6355, 0.0765, 0.072, INNER_WEIGHT),
        labyrinth(0.6426, 0.5977, 0.059, 0.055, INNER_WEIGHT),
        // Кольцо глубоких лабиринтов вокруг поля, по часовой стрелке с севера.
        // Северный и два восточных, а также западный заходят в полосу, где
        // пейзаж уходит в черное: там их свет приглушает затемнение края
        // картинки — так же, как нарисованные круги. Дальний западный
        // (0.083, 0.354) пропущен: он в этой полосе целиком
        labyrinth(0.485, 0.093, 0.061, 0.047),
        labyrinth(0.549, 0.168, 0.034, 0.024),
        labyrinth(0.7393, 0.2012, 0.099, 0.076),
        labyrinth(0.8477, 0.315, 0.05, 0.039),
        labyrinth(0.884, 0.462, 0.066, 0.056),
        labyrinth(0.879, 0.625, 0.047, 0.042),
        labyrinth(0.771, 0.767, 0.07, 0.066),
        labyrinth(0.6187, 0.8345, 0.0757, 0.0635),
        labyrinth(0.207, 0.747, 0.07, 0.066),
        labyrinth(0.112, 0.591, 0.066, 0.055),
        labyrinth(0.211, 0.239, 0.071, 0.061),
        labyrinth(0.3525, 0.144, 0.055, 0.043),
      ]),
    }),
  }),
});
